/**
 * The test send's words and decisions (TEST-EMAIL T3) — pure: no React, no
 * request, no state.
 *
 * EVERY SENTENCE THE READER SEES IS OWNED HERE. The server's own `message` is
 * never shown: a refusal is recognised by its HTTP status and its stable
 * `code`, and answered with a German sentence from this file. That keeps three
 * things off the screen that the wire can carry — the raw JSON text of a rate
 * limiter's `{error}` body, an English "Internal Server Error", and a refusal's
 * internal `reason`. The one exception is the support-mode refusal: its
 * sentence is the server's, and it is recognised by being exactly that
 * sentence, as on the E-Mail-Absender page.
 *
 * NO CLIENT ARITHMETIC ON THE QUOTA. `used_today`, `remaining_today` and
 * `daily_limit` are shown as the server sent them; nothing here subtracts one
 * from the other or decides that a limit is reached. Only the server's
 * `missing_prerequisites` (and its own readiness flags) say a send is blocked.
 *
 * AN ATTEMPT IS NOT A SUCCESS. The daily limit counts reserved ATTEMPTS — a
 * rejected and an unclear send count as much as a delivered one — and an
 * answer that never arrived may still have been a sent e-mail. So a failure
 * after the server may have handed the e-mail to the provider is said to be
 * unclear, never "failed", and nothing here invites an immediate second send.
 */
import { scenarioLabel, templateSourceLabel } from "./automationTemplateLabels";

export const TEST_EMAIL_ACTION_LABEL = "Test-E-Mail senden";
export const TEST_EMAIL_BUSY_LABEL = "Wird gesendet …";
export const TEST_EMAIL_SETTINGS_PATH = "/admin/email-settings";

export const TEST_EMAIL_UNSAVED_NOTE =
  "Speichere die Vorlage zuerst, damit der Testversand genau die gespeicherte Version verwendet.";
export const TEST_EMAIL_SAVING_NOTE =
  "Die Vorlage wird gerade gespeichert. Danach ist der Testversand wieder möglich.";
export const TEST_EMAIL_RUNNING_ELSEWHERE =
  "Eine Test-E-Mail dieser Automatisierung wird gerade gesendet. Das Ergebnis erscheint hier, sobald es vorliegt.";
export const TEST_EMAIL_NO_REAL_MESSAGE =
  "Es wird keine echte automatisierte Nachricht ausgelöst.";
export const TEST_EMAIL_MARKER_NOTE =
  "Die E-Mail wird deutlich als TESTVERSAND gekennzeichnet und verwendet Beispieldaten statt echter Mieter- oder Rechnungsdaten.";
export const TEST_EMAIL_SAVED_TEMPLATE_NOTE =
  "Gesendet wird die gespeicherte Vorlage. Ob der E-Mail-Anbieter die Test-E-Mail annimmt, zeigt sich erst beim Senden.";
export const TEST_EMAIL_LOADING = "Angaben zum Testversand werden geladen …";
export const TEST_EMAIL_SENDING_STATUS = "Die Test-E-Mail wird gesendet …";
export const TEST_EMAIL_RECIPIENT_HINT = "Test-E-Mail-Adresse der Organisation";
export const TEST_EMAIL_SCENARIO_LABEL = "Beispielszenario";
export const TEST_EMAIL_SCENARIO_PLACEHOLDER = "Bitte wählen";
export const TEST_EMAIL_SCENARIO_HINT =
  "Diese Automatisierung versendet verschiedene Briefe. Wähle, welches Beispiel gesendet wird.";
/** A scenario refusal shown where no scenario field is in front of the reader. */
export const TEST_EMAIL_SCENARIO_REOPEN_NOTE =
  "Das Beispielszenario lässt sich nach erneutem Öffnen des Dialogs wählen.";

/** The server's support-mode refusal, shown as it is (and only this one). */
export const SUPPORT_MODE_SENTENCE =
  "Im Support-Modus können keine Test-E-Mails versendet werden.";

export const REJECTED_MESSAGE =
  "Die Test-E-Mail konnte vom E-Mail-Anbieter nicht angenommen werden.";
export const REJECTED_NOTE = "Der Versuch zählt gegen das Tageslimit für Testversände.";
export const INDETERMINATE_MESSAGE =
  "Der Versandstatus ist unklar. Bitte nicht sofort erneut senden.";
export const INDETERMINATE_NOTE =
  "Der Versand könnte trotzdem erfolgt sein und der Versuch zählt gegen das Tageslimit.";
export const UNEXPECTED_MESSAGE = "Beim Testversand ist ein unerwarteter Fehler aufgetreten.";
export const UNEXPECTED_NOTE =
  "Bitte zuerst das Postfach der Testadresse prüfen, bevor erneut gesendet wird.";
export const RATE_LIMITED_MESSAGE =
  "Zu viele Testversand-Versuche in kurzer Zeit. Bitte etwa eine Minute warten, bevor erneut gesendet wird.";
export const PERMISSION_MESSAGE = "Für den Testversand fehlen die nötigen Berechtigungen.";
export const NOT_FOUND_MESSAGE = "Diese Automatisierung wurde nicht gefunden.";
export const INVALID_REQUEST_MESSAGE =
  "Die Anfrage konnte nicht verarbeitet werden. Bitte den Dialog schließen und erneut öffnen.";
export const GENERIC_BLOCKED_MESSAGE = "Ein Testversand ist derzeit nicht möglich.";
export const GENERIC_SEND_MESSAGE = "Die Test-E-Mail konnte nicht gesendet werden.";
export const LOAD_ERROR_MESSAGE = "Die Angaben zum Testversand konnten nicht geladen werden.";

/**
 * What blocks a test send, in the server's own order. The sentences say what
 * is missing, not why the server thinks so: no `reason`, no provider, no key.
 */
export const PREREQUISITE_MESSAGES = {
  test_email_not_supported: "Für diese Automatisierung ist kein Testversand möglich.",
  test_recipient_missing: "Es ist noch keine Test-E-Mail-Adresse hinterlegt.",
  transport_not_configured: "Der E-Mail-Versand ist derzeit nicht eingerichtet.",
  sender_config_missing: "Der E-Mail-Absender ist noch nicht vollständig eingerichtet.",
  test_email_daily_limit_reached: "Das Tageslimit für Testversände wurde erreicht.",
};

const PREREQUISITE_ORDER = Object.keys(PREREQUISITE_MESSAGES);

/** The service's refusals of a send that a scenario choice can still fix. */
const SCENARIO_MESSAGES = {
  scenario_required: "Bitte ein Beispielszenario wählen.",
  unknown_scenario: "Dieses Beispielszenario ist nicht bekannt. Bitte ein anderes wählen.",
};

/** The service's other refusals of a send (422). */
const SEND_REFUSAL_MESSAGES = {
  template_not_renderable:
    "Die gespeicherte Vorlage kann derzeit nicht dargestellt werden. Bitte die Vorlage prüfen.",
  scenario_not_supported: "Für diese Automatisierung gibt es keine Beispielszenarien.",
};

/** The way to the E-Mail-Absender page, where it can fix what is missing. */
function settingsLinkFor(codes) {
  if (codes.includes("test_recipient_missing")) {
    return { label: "Testadresse einrichten", to: TEST_EMAIL_SETTINGS_PATH };
  }
  if (codes.includes("sender_config_missing")) {
    return { label: "E-Mail-Absender einrichten", to: TEST_EMAIL_SETTINGS_PATH };
  }
  return null;
}

/** "Pro Organisation …" — the limit as the server states it, never a constant here. */
export function dailyLimitExplanation(dailyLimit) {
  const limit =
    typeof dailyLimit === "number" && Number.isFinite(dailyLimit) ? dailyLimit : null;
  const cap =
    limit === null
      ? "Testversände sind pro Organisation und Kalendertag (Zürcher Zeit) begrenzt"
      : `Pro Organisation sind höchstens ${limit} Testversand-Versuche pro Kalendertag (Zürcher Zeit) möglich`;
  return `${cap}; auch abgelehnte und unklare Versuche zählen mit.`;
}

const isCount = (value) => typeof value === "number" && Number.isFinite(value);

/**
 * Today's quota in words, or `null` where the server sent no numbers. Shown,
 * never computed: `remaining_today` is the server's, not `limit - used`.
 */
export function quotaText(readiness) {
  const used = readiness?.used_today;
  const limit = readiness?.daily_limit;
  const remaining = readiness?.remaining_today;
  if (!isCount(used) || !isCount(limit) || !isCount(remaining)) return null;
  return `Heute ${used} von ${limit} Versuchen genutzt, noch ${remaining} möglich.`;
}

export const QUOTA_SCOPE_NOTE =
  "Gilt für alle Automatisierungen dieser Organisation; abgelehnte und unklare Versuche zählen mit.";

/** "Eigene Vorlage · Version 3" / "Systemvorlage" — the server's source and number. */
export function templateText(source, version) {
  if (source !== "system" && source !== "custom") return "Nicht bekannt";
  const label = templateSourceLabel(source);
  return version !== null && version !== undefined ? `${label} · Version ${version}` : label;
}

/** A readiness scenario in German (the same shape as the template's scenarios). */
export function testEmailScenarioLabel(scenario) {
  return scenarioLabel(scenario);
}

/**
 * What the readiness read means for the dialog.
 *
 * `blockers` are the missing prerequisites, in the server's order, each once —
 * taken from `missing_prerequisites` and, defensively, from the readiness
 * flags that say the same (a flag the list forgot still blocks). An unknown
 * code blocks with a general sentence instead of being dropped. `canSend` is
 * true only when nothing blocks AND the server says `ready`; `ready` itself is
 * the stored prerequisites only, and the dialog says so.
 */
export function readinessView(readiness) {
  // A type without test sends: the server read nothing for it, so its other
  // flags are not findings about this organisation — one sentence, no link.
  if (readiness?.supported === false) {
    return {
      canSend: false,
      blockers: [
        { code: "test_email_not_supported", message: PREREQUISITE_MESSAGES.test_email_not_supported },
      ],
      dailyLimitReached: false,
      settingsLink: null,
      scenarioRequired: false,
      scenarios: [],
    };
  }
  const listed = Array.isArray(readiness?.missing_prerequisites)
    ? readiness.missing_prerequisites.filter((code) => typeof code === "string")
    : [];
  const codes = new Set(listed);
  if (readiness?.test_recipient_configured === false) codes.add("test_recipient_missing");
  if (readiness?.transport_configured === false) codes.add("transport_not_configured");
  if (readiness?.sender_ready === false) codes.add("sender_config_missing");

  const known = PREREQUISITE_ORDER.filter((code) => codes.has(code));
  const unknown = [...codes].filter((code) => !PREREQUISITE_ORDER.includes(code));
  const blockers = known.map((code) => ({ code, message: PREREQUISITE_MESSAGES[code] }));
  const scenarioRequired = readiness?.scenario_required === true;
  const scenarios = Array.isArray(readiness?.scenarios)
    ? readiness.scenarios.filter((s) => s && typeof s.key === "string" && s.key)
    : [];
  // A choice the server demands but offers nothing to choose from cannot be
  // made here; that is a block, not an empty select.
  const noScenarioToChoose = scenarioRequired && scenarios.length === 0;
  if (
    unknown.length ||
    noScenarioToChoose ||
    (!blockers.length && readiness?.ready !== true)
  ) {
    blockers.push({ code: "unknown", message: GENERIC_BLOCKED_MESSAGE });
  }
  return {
    canSend: blockers.length === 0,
    blockers,
    dailyLimitReached: codes.has("test_email_daily_limit_reached"),
    settingsLink: settingsLinkFor(known),
    scenarioRequired,
    scenarios,
  };
}

/** The success sentence, with the address the SERVER says it sent to. */
export function sentMessage(recipient) {
  return `Test-E-Mail wurde an ${recipient} gesendet.`;
}

const outcomeOf = (fields) => ({
  kind: "refused",
  message: GENERIC_SEND_MESSAGE,
  note: null,
  settingsLink: null,
  dailyLimitNote: false,
  retryable: false,
  attempted: false,
  ...fields,
});

/**
 * A failed send in words.
 *
 * `kind` is `rejected` (the provider refused it), `indeterminate` (nobody knows
 * whether it went out), `error` (the server failed unexpectedly) or `refused`
 * (the server did not try). `attempted` says whether the server may have
 * reached the point of recording an attempt — the Protokoll is re-read only
 * then. `retryable` is true only for a scenario the reader can still choose;
 * every other failure ends the dialog's send.
 */
export function describeSendError(err) {
  const status = typeof err?.status === "number" ? err.status : null;
  const code = typeof err?.code === "string" ? err.code : null;

  // No answer at all: the request may have arrived and the e-mail gone out.
  if (status === null) {
    return outcomeOf({
      kind: "indeterminate",
      message: INDETERMINATE_MESSAGE,
      note: INDETERMINATE_NOTE,
      attempted: true,
    });
  }
  if (status === 502 && code === "provider_rejected") {
    return outcomeOf({
      kind: "rejected",
      message: REJECTED_MESSAGE,
      note: REJECTED_NOTE,
      attempted: true,
    });
  }
  // `provider_indeterminate`, and any gateway failure in front of the server.
  if (status === 502 || status === 503 || status === 504) {
    return outcomeOf({
      kind: "indeterminate",
      message: INDETERMINATE_MESSAGE,
      note: INDETERMINATE_NOTE,
      attempted: true,
    });
  }
  if (status >= 500) {
    return outcomeOf({
      kind: "error",
      message: UNEXPECTED_MESSAGE,
      note: UNEXPECTED_NOTE,
      attempted: true,
    });
  }
  if (status === 429) return outcomeOf({ message: RATE_LIMITED_MESSAGE });
  if (status === 403) {
    const sentence = typeof err?.message === "string" ? err.message.trim() : "";
    return outcomeOf({
      message: sentence === SUPPORT_MODE_SENTENCE ? SUPPORT_MODE_SENTENCE : PERMISSION_MESSAGE,
    });
  }
  if (status === 401) return outcomeOf({ message: PERMISSION_MESSAGE });
  if (status === 404) return outcomeOf({ message: NOT_FOUND_MESSAGE });
  if (status === 409) {
    const known = code && PREREQUISITE_MESSAGES[code] ? code : null;
    return outcomeOf({
      message: known ? PREREQUISITE_MESSAGES[known] : GENERIC_BLOCKED_MESSAGE,
      settingsLink: known ? settingsLinkFor([known]) : null,
      dailyLimitNote: known === "test_email_daily_limit_reached",
    });
  }
  if (status === 422) {
    if (code && SCENARIO_MESSAGES[code]) {
      return outcomeOf({ message: SCENARIO_MESSAGES[code], retryable: true });
    }
    if (code && SEND_REFUSAL_MESSAGES[code]) {
      return outcomeOf({ message: SEND_REFUSAL_MESSAGES[code] });
    }
    // FastAPI's own validation list: no code, English text — never shown.
    return outcomeOf({ message: INVALID_REQUEST_MESSAGE });
  }
  return outcomeOf({ message: GENERIC_SEND_MESSAGE });
}

/**
 * The note under a failure said as an answer — the dialog's outcome or, when
 * no dialog can say it, a toast. Neither has a scenario field beside it, so a
 * scenario refusal says where the choice is made (adversarial review: one
 * place, so the dialog and the toast cannot word it differently).
 */
export function outcomeNote(failure) {
  if (!failure) return null;
  return failure.retryable ? TEST_EMAIL_SCENARIO_REOPEN_NOTE : failure.note;
}

/** A failed readiness read in words; the reader may ask again by hand. */
export function describeLoadError(err) {
  const status = typeof err?.status === "number" ? err.status : null;
  if (status === 401 || status === 403) return PERMISSION_MESSAGE;
  if (status === 404) return NOT_FOUND_MESSAGE;
  return LOAD_ERROR_MESSAGE;
}
